import { useState, useRef, useEffect } from 'react';
import { callJarvis, buildSystemPrompt } from './jarvisApi';

function JarvisChat({ context, onClose, onCreateEvents, onAddTask, onSearchEmail, onReadEmail, onSaveAttachment, onMoveEmail, onSearchVault, onReadVaultDoc, onSearchProcedures, onLoadProcedure, onSearchWorkouts, onLoadWorkout, onProcedureAction, onUpdateInventory, onUpdateDinnerOrder, onCreateDinnerPrep, onAddGroceryItem, onLogPurchase, onSearchPurchases, onSearchDinnerOrders, memory, walkthroughDoc, walkthroughTask }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const attachmentMapRef = useRef({}); // attachmentId -> { name, messageId }
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const walkthroughInitRef = useRef(false);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Auto-start walkthrough if document is provided
  useEffect(() => {
    if (walkthroughDoc && walkthroughTask && !walkthroughInitRef.current) {
      walkthroughInitRef.current = true;
      startWalkthroughChat();
    }
  }, [walkthroughDoc]); // eslint-disable-line react-hooks/exhaustive-deps

  async function startWalkthroughChat() {
    setLoading(true);
    try {
      const walkthroughSystem = `You are John's Big Brain, walking the user through a procedure document step by step. The user is working on: "${walkthroughTask.name}"${walkthroughTask.eventTitle ? ` (for ${walkthroughTask.eventTitle})` : ''}.

Present the document one step at a time. After each step, wait for the user to say "done", "next", or "got it" before showing the next step. If they ask questions about a step, answer them from the document content before continuing. Be clear, concise, and conversational. Number each step.

When you've covered all steps, let the user know the walkthrough is complete.`;

      // Build the first message with document content
      const userContent = [];
      if (walkthroughDoc.type === 'document' || walkthroughDoc.type === 'image') {
        userContent.push(stripFileName(walkthroughDoc));
        userContent.push({ type: 'text', text: 'Walk me through this document step by step.' });
      } else {
        userContent.push({ type: 'text', text: `${walkthroughDoc.text}\n\nWalk me through this step by step.` });
      }

      const response = await callJarvis({
        system: walkthroughSystem,
        messages: [{ role: 'user', content: userContent }],
        max_tokens: 1000,
      });

      const text = response.content?.filter(c => c.type === 'text').map(c => c.text).join('') || '';
      setMessages([
        { role: 'user', content: `Walk me through: ${walkthroughTask.name}` },
        { role: 'assistant', content: text },
      ]);
    } catch (err) {
      setMessages([
        { role: 'assistant', content: `I couldn't load the document. ${err.message}` },
      ]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const systemPrompt = buildSystemPrompt(context);

  async function handleSend(e) {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;

    const userMsg = { role: 'user', content: text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setLoading(true);
    setError(null);

    try {
      // Build API messages (only role + content for Anthropic)
      const apiMessages = newMessages.map(m => ({
        role: m.role,
        content: typeof m.content === 'string' ? m.content : m.content,
      }));

      // Use walkthrough system prompt if in walkthrough mode
      const activeSystem = walkthroughDoc
        ? `You are John's Big Brain, continuing a step-by-step document walkthrough for "${walkthroughTask?.name || 'a procedure'}". Continue from where you left off. When the user says "done", "next", or "got it", present the next step. Answer questions from the document. Number each step.`
        : systemPrompt;

      const response = await callJarvis({ system: activeSystem, messages: apiMessages });

      const assistantText = response.content
        ?.filter(c => c.type === 'text')
        .map(c => c.text)
        .join('') || '';

      // Check for action blocks (may contain multiple actions)
      const allActions = parseActions(assistantText);

      // Process memory operations async (never blocks response display)
      processMemoryOps(assistantText);

      const assistantMsg = { role: 'assistant', content: assistantText };
      setMessages(prev => [...prev, assistantMsg]);

      for (const action of allActions) {
        // Auto-execute actions — no confirmation needed
        if (action.action === 'search_email' && onSearchEmail) {
          await handleEmailSearch(action, [...newMessages, assistantMsg]);
        } else if (action.action === 'read_email' && onReadEmail) {
          await handleEmailRead(action, [...newMessages, assistantMsg]);
        } else if (action.action === 'search_vault' && onSearchVault) {
          await handleVaultSearch(action, [...newMessages, assistantMsg]);
        } else if (action.action === 'read_vault_doc' && onReadVaultDoc) {
          await handleVaultRead(action, [...newMessages, assistantMsg]);
        } else if (action.action === 'search_procedures' && onSearchProcedures) {
          await handleProcedureSearch(action, [...newMessages, assistantMsg]);
        } else if (action.action === 'load_procedure' && onLoadProcedure) {
          await handleProcedureLoad(action, [...newMessages, assistantMsg]);
        } else if (action.action === 'search_workouts' && onSearchWorkouts) {
          await handleWorkoutSearch(action, [...newMessages, assistantMsg]);
        } else if (action.action === 'load_workout' && onLoadWorkout) {
          await handleWorkoutLoad(action, [...newMessages, assistantMsg]);
        } else if (action.action === 'update_inventory' && onUpdateInventory) {
          try {
            await onUpdateInventory(action.items);
            setMessages(prev => [...prev, { role: 'assistant', content: action.confirmation || 'Inventory updated.' }]);
          } catch (err) {
            setMessages(prev => [...prev, { role: 'assistant', content: `Couldn't update inventory: ${err.message}` }]);
          }
        } else if (action.action === 'add_grocery_item' && onAddGroceryItem) {
          try {
            await onAddGroceryItem(action);
            setMessages(prev => [...prev, { role: 'assistant', content: action.confirmation || 'Added to shopping list.' }]);
          } catch (err) {
            setMessages(prev => [...prev, { role: 'assistant', content: `Couldn't add to shopping list: ${err.message}` }]);
          }
        } else if (action.action === 'log_purchase' && onLogPurchase) {
          try {
            await onLogPurchase(action);
            setMessages(prev => [...prev, { role: 'assistant', content: action.confirmation || 'Purchase logged.' }]);
          } catch (err) {
            setMessages(prev => [...prev, { role: 'assistant', content: `Couldn't log purchase: ${err.message}` }]);
          }
        } else if (action.action === 'search_purchases' && onSearchPurchases) {
          try {
            const results = await onSearchPurchases(action.itemType, action.period);
            const contextMsg = { role: 'user', content: `[PURCHASE DATA]\n${results}` };
            const apiMsgs = [...[...newMessages, assistantMsg], contextMsg].map(m => ({ role: m.role, content: m.content }));
            const followUp = await callJarvis({ system: buildSystemPrompt(context), messages: apiMsgs });
            const followUpText = followUp.content?.filter(c => c.type === 'text').map(c => c.text).join('') || '';
            processMemoryOps(followUpText);
            setMessages(prev => [...prev, { role: 'assistant', content: followUpText }]);
          } catch (err) {
            setMessages(prev => [...prev, { role: 'assistant', content: `Couldn't load purchase data: ${err.message}` }]);
          }
        } else if (action.action === 'search_dinner_orders' && onSearchDinnerOrders) {
          try {
            const results = await onSearchDinnerOrders(action.period);
            const contextMsg = { role: 'user', content: `[DINNER ORDER HISTORY]\n${results}` };
            const apiMsgs = [...[...newMessages, assistantMsg], contextMsg].map(m => ({ role: m.role, content: m.content }));
            const followUp = await callJarvis({ system: buildSystemPrompt(context), messages: apiMsgs });
            const followUpText = followUp.content?.filter(c => c.type === 'text').map(c => c.text).join('') || '';
            processMemoryOps(followUpText);
            setMessages(prev => [...prev, { role: 'assistant', content: followUpText }]);
          } catch (err) {
            setMessages(prev => [...prev, { role: 'assistant', content: `Couldn't load dinner history: ${err.message}` }]);
          }
        } else if (action.action === 'update_dinner_order' && onUpdateDinnerOrder) {
          try {
            await onUpdateDinnerOrder(action);
            setMessages(prev => [...prev, { role: 'assistant', content: action.confirmation || 'Dinner order updated.' }]);
          } catch (err) {
            setMessages(prev => [...prev, { role: 'assistant', content: `Couldn't update dinner order: ${err.message}` }]);
          }
        } else if (['edit_procedure_step', 'add_procedure_step', 'delete_procedure_step', 'reorder_procedure_step'].includes(action.action) && onProcedureAction) {
          try {
            await onProcedureAction(action);
            setMessages(prev => [...prev, { role: 'assistant', content: action.confirmation || 'Done.' }]);
          } catch (err) {
            setMessages(prev => [...prev, { role: 'assistant', content: `Couldn't do that: ${err.message}` }]);
          }
        } else {
          // Confirmation-required actions (create_event, add_task, save_attachment, etc.)
          setPendingAction(action);
        }
      }
    } catch (err) {
      setError("John's Big Brain is unavailable right now. Try again in a moment.");
      setMessages(prev => [...prev, { role: 'assistant', content: 'I\'m having trouble connecting. Try again in a moment.' }]);
    } finally {
      setLoading(false);
    }
  }

  async function handleEmailSearch(action, currentMessages) {
    try {
      const results = await onSearchEmail(action.query, action.maxResults || 10, action.recipientFilter || null);

      // Fetch real attachment names for emails that have them
      for (const r of results) {
        if (r.hasAttachments) {
          try {
            const atts = await onReadEmail(r.id, true);
            r.attachmentList = (atts || []).map(a => ({ id: a.id, name: a.name }));
            // Cache real names
            (atts || []).forEach(a => {
              attachmentMapRef.current[a.id] = { name: a.name, messageId: r.id };
            });
          } catch { r.attachmentList = []; }
        }
      }

      const resultsText = results.length === 0
        ? 'No emails found matching that search.'
        : results.map((r, i) => {
            let line = `${i + 1}. From: ${r.from} | To: ${(r.to || []).join(', ') || 'unknown'} | Subject: "${r.subject}" | Date: ${new Date(r.date).toLocaleDateString()} | ID: ${r.id}`;
            if (r.attachmentList && r.attachmentList.length > 0) {
              line += ` | Attachments: ${r.attachmentList.map(a => `"${a.name}" (attachmentId:${a.id})`).join(', ')}`;
            } else if (r.hasAttachments) {
              line += ' | Attachments: Yes';
            }
            return line;
          }).join('\n');

      // Feed results back to JBB as a user message (hidden from UI)
      const contextMsg = { role: 'user', content: `[EMAIL SEARCH RESULTS]\n${resultsText}` };
      const apiMsgs = [...currentMessages, contextMsg].map(m => ({
        role: m.role,
        content: typeof m.content === 'string' ? m.content : m.content,
      }));

      const activeSystem = walkthroughDoc
        ? systemPrompt
        : buildSystemPrompt(context);
      const followUp = await callJarvis({ system: activeSystem, messages: apiMsgs, max_tokens: 800 });
      const followUpText = followUp.content?.filter(c => c.type === 'text').map(c => c.text).join('') || '';

      const followUpAction = parseAction(followUpText);
      processMemoryOps(followUpText);
      setMessages(prev => [...prev, { role: 'assistant', content: followUpText, _isEmailResults: true }]);
      if (followUpAction) setPendingAction(followUpAction);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Couldn't search emails: ${err.message}` }]);
    }
  }

  async function handleEmailRead(action, currentMessages) {
    try {
      const email = await onReadEmail(action.messageId);
      const emailText = `Subject: "${email.subject}"\nFrom: ${email.from} (${email.fromEmail})\nDate: ${new Date(email.date).toLocaleDateString()}\nPreview: ${email.preview}`;

      let attachmentInfo = '';
      if (email.hasAttachments) {
        const attachments = await onReadEmail(action.messageId, true);
        if (attachments && attachments.length) {
          attachmentInfo = '\nAttachments: ' + attachments.map(a => `"${a.name}" (attachmentId:${a.id})`).join(', ');
          // Cache real names
          attachments.forEach(a => {
            attachmentMapRef.current[a.id] = { name: a.name, messageId: action.messageId };
          });
        }
      }

      const contextMsg = { role: 'user', content: `[EMAIL CONTENT]\n${emailText}${attachmentInfo}` };
      const apiMsgs = [...currentMessages, contextMsg].map(m => ({
        role: m.role,
        content: typeof m.content === 'string' ? m.content : m.content,
      }));

      const activeSystem = walkthroughDoc
        ? systemPrompt
        : buildSystemPrompt(context);
      const followUp = await callJarvis({ system: activeSystem, messages: apiMsgs, max_tokens: 800 });
      const followUpText = followUp.content?.filter(c => c.type === 'text').map(c => c.text).join('') || '';

      const followUpAction = parseAction(followUpText);
      processMemoryOps(followUpText);
      setMessages(prev => [...prev, { role: 'assistant', content: followUpText }]);
      if (followUpAction) setPendingAction(followUpAction);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Couldn't read that email: ${err.message}` }]);
    }
  }

  function processMemoryOps(text) {
    if (!memory) return;
    const match = text.match(/```memory_ops\n([\s\S]*?)```/);
    if (!match) return;
    let ops;
    try { ops = JSON.parse(match[1]); } catch { return; }

    // Process async — never blocks UI
    (async () => {
      try {
        // Save new facts
        if (ops.save && ops.save.length > 0) {
          for (const item of ops.save) {
            if (!item.fact) continue;
            if (item.type === 'core') {
              await memory.saveCore(item.fact, 'manual');
            } else {
              await memory.saveWorking(item.fact, item.expires || null);
            }
          }
        }

        // Mark used memories
        if (ops.used && ops.used.length > 0) {
          await memory.markUsed(ops.used);
        }

        // Handle contradiction
        if (ops.contradicts && ops.contradicts.existingId && ops.contradicts.newFact) {
          await memory.resolveContradiction(ops.contradicts.existingId, ops.contradicts.newFact);
        }
      } catch (err) {
        console.warn('Memory ops processing error:', err.message);
      }
    })();
  }

  async function handleVaultSearch(action, currentMessages) {
    try {
      const results = await onSearchVault(action.query);
      const resultsText = results.length === 0
        ? 'No files found in the Vault matching that search.'
        : results.map((r, i) =>
            `${i + 1}. ${r.name} | Folder: ${r.folder || 'root'} | ID: ${r.id} | Modified: ${r.lastModified || ''}`
          ).join('\n');

      const contextMsg = { role: 'user', content: `[VAULT SEARCH RESULTS]\n${resultsText}` };
      const apiMsgs = [...currentMessages, contextMsg].map(m => ({
        role: m.role,
        content: typeof m.content === 'string' ? m.content : m.content,
      }));

      const activeSystem = buildSystemPrompt(context);
      const followUp = await callJarvis({ system: activeSystem, messages: apiMsgs, max_tokens: 800 });
      const followUpText = followUp.content?.filter(c => c.type === 'text').map(c => c.text).join('') || '';

      const followUpAction = parseAction(followUpText);
      processMemoryOps(followUpText);
      setMessages(prev => [...prev, { role: 'assistant', content: followUpText }]);
      if (followUpAction) {
        if (['search_vault', 'read_vault_doc'].includes(followUpAction.action)) {
          // Chain vault actions
          if (followUpAction.action === 'read_vault_doc' && onReadVaultDoc) {
            await handleVaultRead(followUpAction, [...currentMessages, contextMsg, { role: 'assistant', content: followUpText }]);
          }
        } else {
          setPendingAction(followUpAction);
        }
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Couldn't search the Vault: ${err.message}` }]);
    }
  }

  async function handleVaultRead(action, currentMessages) {
    try {
      const docContent = await onReadVaultDoc(action.fileId, action.fileName);

      // Build the context message with actual document content (not a placeholder)
      let contextMsg;
      if (docContent.type === 'document' || docContent.type === 'image') {
        // Send the actual PDF/image content as multipart — same pattern as walkthrough
        contextMsg = {
          role: 'user',
          content: [
            stripFileName(docContent),
            { type: 'text', text: `[VAULT DOCUMENT LOADED: ${action.fileName || docContent._fileName || 'file'}]\nThe document content is above. Read it carefully and use the EXACT content for any conversion or walkthrough.` },
          ],
        };
      } else {
        contextMsg = { role: 'user', content: `[VAULT DOCUMENT LOADED]\n${docContent.text}` };
      }

      const apiMsgs = [...currentMessages, contextMsg].map(m => ({
        role: m.role,
        content: typeof m.content === 'string' ? m.content : m.content,
      }));

      const activeSystem = buildSystemPrompt(context);
      const followUp = await callJarvis({ system: activeSystem, messages: apiMsgs, max_tokens: 1000 });
      const followUpText = followUp.content?.filter(c => c.type === 'text').map(c => c.text).join('') || '';

      const followUpAction = parseAction(followUpText);
      processMemoryOps(followUpText);
      setMessages(prev => [...prev, { role: 'assistant', content: followUpText }]);
      if (followUpAction) setPendingAction(followUpAction);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Couldn't load that document: ${err.message}` }]);
    }
  }

  async function handleProcedureSearch(action, currentMessages) {
    try {
      const results = await onSearchProcedures(action.query);
      const resultsText = results.length === 0
        ? 'No procedures found matching that search.'
        : results.map((r, i) =>
            `${i + 1}. "${r.name}" | Category: ${r.category || 'none'} | ${r.steps.length} steps | Status: ${r.conversionStatus || 'complete'} | ID: ${r.id}`
          ).join('\n');

      const contextMsg = { role: 'user', content: `[PROCEDURE SEARCH RESULTS]\n${resultsText}` };
      const apiMsgs = [...currentMessages, contextMsg].map(m => ({
        role: m.role,
        content: typeof m.content === 'string' ? m.content : m.content,
      }));

      const activeSystem = buildSystemPrompt(context);
      const followUp = await callJarvis({ system: activeSystem, messages: apiMsgs, max_tokens: 800 });
      const followUpText = followUp.content?.filter(c => c.type === 'text').map(c => c.text).join('') || '';

      const followUpAction = parseAction(followUpText);
      processMemoryOps(followUpText);
      setMessages(prev => [...prev, { role: 'assistant', content: followUpText }]);
      if (followUpAction) {
        if (followUpAction.action === 'load_procedure' && onLoadProcedure) {
          await handleProcedureLoad(followUpAction, [...currentMessages, contextMsg, { role: 'assistant', content: followUpText }]);
        } else {
          setPendingAction(followUpAction);
        }
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Couldn't search procedures: ${err.message}` }]);
    }
  }

  async function handleProcedureLoad(action, currentMessages) {
    try {
      const proc = await onLoadProcedure(action.procedureId);
      if (!proc) throw new Error('Procedure not found');

      const stepsText = proc.steps.map(s =>
        `Step ${s.stepNumber}: ${s.title}\nDetails: ${s.details || '(none)'}${s.notes ? '\nNote: ' + s.notes : ''}`
      ).join('\n\n');

      const contextText = `[PROCEDURE LOADED]\nName: ${proc.name}\nCategory: ${proc.category || 'none'}\nID: ${proc.id}\nStatus: ${proc.conversionStatus || 'complete'}\nTotal steps: ${proc.steps.length}\n\n${stepsText}`;

      const contextMsg = { role: 'user', content: contextText };
      const apiMsgs = [...currentMessages, contextMsg].map(m => ({
        role: m.role,
        content: typeof m.content === 'string' ? m.content : m.content,
      }));

      const activeSystem = buildSystemPrompt(context);
      const followUp = await callJarvis({ system: activeSystem, messages: apiMsgs, max_tokens: 1000 });
      const followUpText = followUp.content?.filter(c => c.type === 'text').map(c => c.text).join('') || '';

      const followUpAction = parseAction(followUpText);
      processMemoryOps(followUpText);
      setMessages(prev => [...prev, { role: 'assistant', content: followUpText }]);
      if (followUpAction) setPendingAction(followUpAction);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Couldn't load procedure: ${err.message}` }]);
    }
  }

  async function handleWorkoutSearch(action, currentMessages) {
    try {
      const results = await onSearchWorkouts(action.period || 'week');
      const contextMsg = { role: 'user', content: `[SWIM WORKOUT DATA]\n${results}` };
      const apiMsgs = [...currentMessages, contextMsg].map(m => ({
        role: m.role, content: typeof m.content === 'string' ? m.content : m.content,
      }));
      const activeSystem = buildSystemPrompt(context);
      const followUp = await callJarvis({ system: activeSystem, messages: apiMsgs, max_tokens: 800 });
      const followUpText = followUp.content?.filter(c => c.type === 'text').map(c => c.text).join('') || '';
      const followUpAction = parseAction(followUpText);
      processMemoryOps(followUpText);
      setMessages(prev => [...prev, { role: 'assistant', content: followUpText }]);
      if (followUpAction) {
        if (followUpAction.action === 'load_workout' && onLoadWorkout) {
          await handleWorkoutLoad(followUpAction, [...currentMessages, contextMsg, { role: 'assistant', content: followUpText }]);
        } else { setPendingAction(followUpAction); }
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Couldn't load workout data: ${err.message}` }]);
    }
  }

  async function handleWorkoutLoad(action, currentMessages) {
    try {
      const result = await onLoadWorkout(action.workoutId);
      const contextMsg = { role: 'user', content: `[WORKOUT DETAIL]\n${result}` };
      const apiMsgs = [...currentMessages, contextMsg].map(m => ({
        role: m.role, content: typeof m.content === 'string' ? m.content : m.content,
      }));
      const activeSystem = buildSystemPrompt(context);
      const followUp = await callJarvis({ system: activeSystem, messages: apiMsgs, max_tokens: 800 });
      const followUpText = followUp.content?.filter(c => c.type === 'text').map(c => c.text).join('') || '';
      processMemoryOps(followUpText);
      setMessages(prev => [...prev, { role: 'assistant', content: followUpText }]);
      const followUpAction = parseAction(followUpText);
      if (followUpAction) setPendingAction(followUpAction);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Couldn't load workout details: ${err.message}` }]);
    }
  }

  async function handleConfirmAction() {
    if (!pendingAction) return;
    setLoading(true);
    try {
      if (pendingAction.action === 'create_event' && onCreateEvents) {
        await onCreateEvents(pendingAction.events);
        const count = pendingAction.events.length;
        const hasRecurrence = pendingAction.events.some(e => e.recurrence);
        const msg = hasRecurrence
          ? 'Done! Recurring event created on the Family calendar. Want to attach a mission template to this?'
          : `Done! ${count > 1 ? count + ' events' : 'Event'} created on the Family calendar. Want to attach a mission template?`;
        setMessages(prev => [...prev, { role: 'assistant', content: msg }]);
      } else if (pendingAction.action === 'add_task' && onAddTask) {
        await onAddTask(pendingAction.eventTitle, pendingAction.task);
        setMessages(prev => [...prev, { role: 'assistant', content: `Added "${pendingAction.task.name}" to ${pendingAction.eventTitle}.` }]);
      } else if (pendingAction.action === 'save_attachment' && onSaveAttachment) {
        // Use the real filename from the API cache, not JBB's generated name
        const cached = attachmentMapRef.current[pendingAction.attachmentId];
        const realName = cached?.name || pendingAction.attachmentName;
        const result = await onSaveAttachment(
          pendingAction.messageId,
          pendingAction.attachmentId,
          realName,
          pendingAction.vaultFolder
        );
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `Done! ${result.name} is saved in your Vault.`,
        }]);
      } else if (pendingAction.action === 'move_email' && onMoveEmail) {
        await onMoveEmail(pendingAction.messageId, pendingAction.folderName);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `Moved to ${pendingAction.folderName}.`,
        }]);
      } else if (pendingAction.action === 'create_dinner_prep' && onCreateDinnerPrep) {
        await onCreateDinnerPrep(pendingAction.event, pendingAction.tasks);
        const taskCount = pendingAction.tasks?.length || 0;
        setMessages(prev => [...prev, { role: 'assistant', content: `Done! Dinner event created with ${taskCount} prep tasks on the Family calendar.` }]);
      } else if (pendingAction.action === 'propose_steps' && onProcedureAction) {
        await onProcedureAction(pendingAction);
        const count = pendingAction.steps?.length || 0;
        const msg = pendingAction.isLastBatch
          ? `Saved ${count} steps. Conversion complete!`
          : `Saved ${count} steps. Ready for the next batch — say "continue" or "pause".`;
        setMessages(prev => [...prev, { role: 'assistant', content: msg }]);
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Couldn't complete that — ${err.message}` }]);
    } finally {
      setPendingAction(null);
      setLoading(false);
    }
  }

  function handleRejectAction() {
    setPendingAction(null);
    setMessages(prev => [...prev, { role: 'assistant', content: 'No problem, cancelled.' }]);
  }

  function copyToClipboard(text) {
    navigator.clipboard.writeText(text).catch(() => {});
  }

  function renderMessage(msg, i) {
    const isUser = msg.role === 'user';
    // Strip action code blocks from display
    const display = typeof msg.content === 'string'
      ? msg.content
          .replace(/```(?:create_event|add_task|search_email|read_email|save_attachment|move_email|memory_ops|propose_steps|edit_procedure_step|add_procedure_step|delete_procedure_step|reorder_procedure_step|search_vault|read_vault_doc|search_procedures|load_procedure|search_workouts|load_workout|update_inventory|update_dinner_order|create_dinner_prep|add_grocery_item|log_purchase|search_purchases|search_dinner_orders|json)\n[\s\S]*?```/g, '')
          .replace(/\{\s*"action"\s*:\s*"(?:search_vault|read_vault_doc|search_procedures|load_procedure|search_workouts|load_workout|search_email|read_email|save_attachment|move_email|create_event|add_task|propose_steps|edit_procedure_step|add_procedure_step|delete_procedure_step|reorder_procedure_step|update_inventory|update_dinner_order|create_dinner_prep|add_grocery_item|log_purchase|search_purchases|search_dinner_orders)"[\s\S]*?\}/g, '')
          .trim()
      : msg.content;

    if (!display) return null;

    return (
      <div key={i} className={`Jarvis-msg${isUser ? ' Jarvis-msg--user' : ' Jarvis-msg--assistant'}`}>
        <div className="Jarvis-msg-bubble">
          {renderContent(display)}
        </div>
      </div>
    );
  }

  function renderContent(text) {
    // Split on fenced code blocks first: ```lang\ncode\n```
    const parts = text.split(/(```[\s\S]*?```)/g);
    return parts.map((part, pi) => {
      const fencedMatch = part.match(/^```(?:\w*)\n([\s\S]*?)```$/);
      if (fencedMatch) {
        const code = fencedMatch[1].trim();
        return (
          <div key={pi} className="Jarvis-code-block">
            <pre className="Jarvis-code-pre">{code}</pre>
            <CopyBtn text={code} />
          </div>
        );
      }
      // Render lines, detecting inline `code`
      return part.split('\n').map((line, li) => (
        <p key={`${pi}-${li}`} className="Jarvis-msg-line">{renderInlineCode(line)}</p>
      ));
    });
  }

  function renderInlineCode(line) {
    const parts = line.split(/(`[^`]+`)/g);
    if (parts.length === 1) return line;
    return parts.map((seg, si) => {
      const inlineMatch = seg.match(/^`([^`]+)`$/);
      if (inlineMatch) {
        const code = inlineMatch[1];
        return (
          <span key={si} className="Jarvis-inline-code-wrap">
            <code className="Jarvis-inline-code">{code}</code>
            <CopyBtn text={code} />
          </span>
        );
      }
      return seg;
    });
  }

  return (
    <div className="Jarvis-overlay" onClick={onClose}>
      <div className="Jarvis-panel" onClick={e => e.stopPropagation()}>
        <div className="Jarvis-header">
          <span className="Jarvis-header-title">John's Big Brain</span>
          <button className="Jarvis-close" onClick={onClose}>&times;</button>
        </div>

        <div className="Jarvis-messages">
          {messages.length === 0 && (
            <div className="Jarvis-welcome">
              <p className="Jarvis-welcome-text">How can I help, {context.nickname || 'boss'}?</p>
              <div className="Jarvis-suggestions">
                <button className="Jarvis-suggestion" onClick={() => { setInput("What's on the schedule today?"); }}>Today's schedule</button>
                <button className="Jarvis-suggestion" onClick={() => { setInput("What should I focus on?"); }}>What to focus on</button>
                <button className="Jarvis-suggestion" onClick={() => { setInput("Am I forgetting anything?"); }}>Am I forgetting anything?</button>
              </div>
            </div>
          )}
          {messages.map((msg, i) => renderMessage(msg, i))}
          {loading && (
            <div className="Jarvis-msg Jarvis-msg--assistant">
              <div className="Jarvis-msg-bubble Jarvis-msg-bubble--thinking">
                <span className="Jarvis-thinking-dots">...</span>
              </div>
            </div>
          )}
          {pendingAction && !loading && (
            <div className="Jarvis-action-card">
              {pendingAction.action === 'create_event' && pendingAction.events && (
                <div className="Jarvis-action-events">
                  {pendingAction.events.map((ev, ei) => (
                    <div key={ei} className="Jarvis-action-event-detail">
                      <strong>{ev.subject}</strong>
                      <span>{formatEventTime(ev.startDateTime)} – {formatEventTime(ev.endDateTime)}</span>
                      {ev.location && <span>{ev.location}</span>}
                      {ev.recurrence && <span className="Jarvis-action-recurrence">Recurring: {describeRecurrence(ev.recurrence)}</span>}
                    </div>
                  ))}
                </div>
              )}
              {pendingAction.action === 'add_task' && (
                <p className="Jarvis-action-label">Add "{pendingAction.task?.name}" to {pendingAction.eventTitle}?</p>
              )}
              {pendingAction.action === 'save_attachment' && (
                <p className="Jarvis-action-label">
                  Save "{attachmentMapRef.current[pendingAction.attachmentId]?.name || pendingAction.attachmentName}" to {pendingAction.vaultFolder || 'Vault root'}?
                </p>
              )}
              {pendingAction.action === 'move_email' && (
                <p className="Jarvis-action-label">
                  Move email to {pendingAction.folderName}?
                </p>
              )}
              {pendingAction.action === 'create_dinner_prep' && (
                <div className="Jarvis-action-events">
                  <p className="Jarvis-action-label"><strong>{pendingAction.event?.subject}</strong> at {formatEventTime(pendingAction.event?.startDateTime)}</p>
                  {(pendingAction.tasks || []).map((t, ti) => (
                    <div key={ti} className="Jarvis-action-event-detail">
                      <strong>{t.name}</strong>
                      {t.notes && <span>{t.notes}</span>}
                    </div>
                  ))}
                </div>
              )}
              {pendingAction.action === 'propose_steps' && (
                <div className="Jarvis-action-events">
                  <p className="Jarvis-action-label">Save {pendingAction.steps?.length || 0} steps?</p>
                  {(pendingAction.steps || []).map((s, si) => (
                    <div key={si} className="Jarvis-action-event-detail">
                      <strong>{si + 1}. {s.title}</strong>
                      {s.details && <span>{s.details.substring(0, 80)}{s.details.length > 80 ? '...' : ''}</span>}
                    </div>
                  ))}
                </div>
              )}
              <div className="Jarvis-action-btns">
                <button className="Jarvis-action-confirm" onClick={handleConfirmAction}>Confirm</button>
                <button className="Jarvis-action-cancel" onClick={handleRejectAction}>Cancel</button>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <form className="Jarvis-input-bar" onSubmit={handleSend}>
          <input
            ref={inputRef}
            className="Jarvis-input"
            type="text"
            placeholder="Ask John's Big Brain..."
            value={input}
            onChange={e => setInput(e.target.value)}
            disabled={loading}
          />
          <button className="Jarvis-send" type="submit" disabled={loading || !input.trim()}>
            &#9654;
          </button>
        </form>
      </div>
    </div>
  );
}

function formatEventTime(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  return d.toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function describeRecurrence(rec) {
  if (!rec?.pattern) return 'recurring';
  const days = rec.pattern.daysOfWeek;
  if (days && days.length) {
    const dayNames = days.map(d => d.charAt(0).toUpperCase() + d.slice(1));
    return `every ${dayNames.join(' & ')}`;
  }
  if (rec.pattern.type === 'daily') return `every ${rec.pattern.interval || 1} day(s)`;
  if (rec.pattern.type === 'weekly') return `weekly`;
  return 'recurring';
}

const ACTION_TYPES = ['create_event', 'add_task', 'search_email', 'read_email', 'save_attachment', 'move_email', 'search_vault', 'read_vault_doc', 'search_procedures', 'load_procedure', 'search_workouts', 'load_workout', 'propose_steps', 'edit_procedure_step', 'add_procedure_step', 'delete_procedure_step', 'reorder_procedure_step', 'update_inventory', 'update_dinner_order', 'create_dinner_prep', 'add_grocery_item', 'log_purchase', 'search_purchases', 'search_dinner_orders'];

function parseActions(text) {
  const actions = [];
  const seen = new Set();
  // Try code-fenced actions first
  for (const type of ACTION_TYPES) {
    const match = text.match(new RegExp('```' + type + '\\n([\\s\\S]*?)```'));
    if (match) {
      try {
        const parsed = JSON.parse(match[1]);
        actions.push(parsed);
        seen.add(type);
      } catch { /* ignore */ }
    }
  }
  // Also try generic ```json fences containing an action field
  const jsonMatches = text.matchAll(/```json\n([\s\S]*?)```/g);
  for (const jsonMatch of jsonMatches) {
    try {
      const parsed = JSON.parse(jsonMatch[1]);
      if (parsed.action && ACTION_TYPES.includes(parsed.action) && !seen.has(parsed.action)) {
        actions.push(parsed);
        seen.add(parsed.action);
      }
    } catch { /* ignore */ }
  }
  // Fallback: detect bare JSON blocks with an action field (no code fence)
  if (actions.length === 0) {
    const bareMatch = text.match(/\{\s*"action"\s*:\s*"(\w+)"[\s\S]*?\}/);
    if (bareMatch) {
      try {
        const parsed = JSON.parse(bareMatch[0]);
        if (parsed.action && ACTION_TYPES.includes(parsed.action)) {
          actions.push(parsed);
        }
      } catch { /* ignore */ }
    }
  }
  return actions;
}

// Backward-compatible single-action wrapper
function parseAction(text) {
  const actions = parseActions(text);
  return actions.length > 0 ? actions[0] : null;
}

function stripFileName(obj) {
  if (!obj || !obj._fileName) return obj;
  const { _fileName, ...clean } = obj;
  return clean;
}

function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className="Jarvis-copy-btn"
      onClick={e => {
        e.stopPropagation();
        navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }).catch(() => {});
      }}
    >
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

export default JarvisChat;
